var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Shipment Assembly Task Card
 * Restores assembly visibility and table details in a compact card.
 */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Table from '@fp-suite/core-ui/src/components/Table';
import { usePackingState } from '../../../hooks/packing';
import { selectAssemblyProducts, selectShipmentData } from '../../../redux/packing';
export var ShipmentAssemblyTaskPane = function () {
    var _a;
    var t = useTranslation().t;
    var kitComponentProducts = usePackingState().kitComponentProducts;
    var shipmentData = useSelector(selectShipmentData);
    var assemblyProducts = useSelector(selectAssemblyProducts);
    var showAssemblyTask = useMemo(
    // TODO // NOTE
    // Condition used to be quantityTotal>0, but was always zero when no quantity was issued yet. 
    // totalQuantity is returned from the backend as the actual total quantity.
    // quantityTotal is the sum of all quantities issued. Misleading naming, but artifact of old code.
    function () { return (kitComponentProducts || []).some(function (item) { return (item === null || item === void 0 ? void 0 : item.totalQuantity) > 0; }); }, [kitComponentProducts]);
    var assemblyTask = useMemo(function () {
        return (assemblyProducts || []).reduce(function (acc, cur) {
            acc.estimatedQuantity += cur.estimatedQuantity || 0;
            acc.completed += cur.completed || 0;
            return acc;
        }, { estimatedQuantity: 0, completed: 0 });
    }, [assemblyProducts]);
    var uniqueKitComponentProducts = useMemo(function () {
        return (kitComponentProducts || []).filter(function (item, index, list) {
            return index ===
                list.findIndex(function (product) { return product.componentProductId === item.componentProductId; });
        });
    }, [kitComponentProducts]);
    var kitProductAssemblyColumns = useMemo(function () { return [
        {
            title: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Product') }), void 0),
            render: function (productId, data) { return (_jsxs("span", __assign({ className: 'font-medium text-sm' }, { children: [productId, ": ", data === null || data === void 0 ? void 0 : data.productName] }), void 0)); },
            dataIndex: 'productId'
        },
        {
            title: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Total') }), void 0),
            render: function (quantity) { return _jsx("span", __assign({ className: 'font-medium text-sm' }, { children: quantity }), void 0); },
            dataIndex: 'estimatedQuantity'
        },
        {
            title: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Completed') }), void 0),
            render: function (completed, data) { return (_jsx("span", __assign({ className: "font-bold " + (completed === (data === null || data === void 0 ? void 0 : data.estimatedQuantity) ? 'text-green-500' : 'text-red-500') }, { children: completed }), void 0)); },
            dataIndex: 'completed'
        }
    ]; }, [t]);
    var kitComponentColumns = useMemo(function () { return [
        {
            title: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Component Product') }), void 0),
            render: function (productId, data) {
                var _a, _b;
                return (_jsx("span", __assign({ className: 'font-medium text-sm' }, { children: ((_b = (_a = data === null || data === void 0 ? void 0 : data.sku) !== null && _a !== void 0 ? _a : data === null || data === void 0 ? void 0 : data.upc) !== null && _b !== void 0 ? _b : productId) + ": " + ((data === null || data === void 0 ? void 0 : data.productName) || '') }), void 0));
            },
            dataIndex: 'componentProductId'
        },
        {
            title: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Total') }), void 0),
            render: function (totalQuantity) { return (_jsx("span", __assign({ className: 'font-medium text-sm' }, { children: totalQuantity }), void 0)); },
            dataIndex: 'quantity'
        },
        {
            title: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Packed') }), void 0),
            render: function (packedQuantity, data) { return (_jsx("span", __assign({ className: "font-bold " + (packedQuantity === (data === null || data === void 0 ? void 0 : data.quantity) ? 'text-green-500' : 'text-red-500') }, { children: packedQuantity }), void 0)); },
            dataIndex: 'totalQuantity'
        },
        {
            title: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Used') }), void 0),
            render: function (usedQuantity, data) { return (_jsx("span", __assign({ className: "font-bold " + (usedQuantity === (data === null || data === void 0 ? void 0 : data.quantity) ? 'text-green-500' : 'text-red-500') }, { children: usedQuantity }), void 0)); },
            dataIndex: 'usedQuantity'
        },
        {
            title: _jsx("p", __assign({ className: 'mr-1' }, { children: t('Unused') }), void 0),
            render: function (unusedQty) { return (_jsx("span", __assign({ className: "font-bold " + (unusedQty === 0 ? 'text-green-500' : 'text-red-500') }, { children: unusedQty }), void 0)); },
            dataIndex: 'unUsedQuantity'
        }
    ]; }, [t]);
    if (!showAssemblyTask) {
        return null;
    }
    return (_jsxs("div", { children: [_jsxs("p", __assign({ className: "mb-2 text-xs font-bold uppercase " + (assemblyTask.estimatedQuantity === assemblyTask.completed
                    ? 'text-green-600'
                    : 'text-red-600') }, { children: [t('Task #'), ((_a = shipmentData === null || shipmentData === void 0 ? void 0 : shipmentData.assemblyWorkEffort) === null || _a === void 0 ? void 0 : _a.workEffortId) || 'N/A'] }), void 0), _jsxs("div", __assign({ className: 'space-y-2' }, { children: [_jsx(Table, { columns: kitProductAssemblyColumns, dataSource: assemblyProducts || [], isPaginated: false }, 'shipment-assembly-products'), _jsx(Table, { columns: kitComponentColumns, dataSource: uniqueKitComponentProducts, isPaginated: false }, 'shipment-assembly-components')] }), void 0)] }, void 0));
};
export default ShipmentAssemblyTaskPane;
